import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  changeDateFilter,
  changeLimitFilter,
  changePlayernameToFind,
  changeTableSizeFilter,
  dropFilters,
  dropFiltersToSettingsState,
  findStats,
  setLoadedBaseStats,
  setLoadedGraphStats,
  setLoadedPlayers,
  showGraphError,
  showLoadingGraphSpinner,
} from './actions';
import AutocompletePlayerModel from '../../viewModel/AutocompletePlayerModel';
import type { FullStatsDto, ShortStatsDto } from '@poker-stats/contracts';
import type { PlayerDto } from '@poker-stats/contracts';
import { pokerSitePostfix } from './utils';
import { getPresetFiltersLocalStorage } from '../../localStorage/presetFiltersLocalStorage';
import type { PlayerGraphResultsDto } from '@poker-stats/contracts';

const { tableSizeFilterValue, limitFilterValue, customDateFilterValue, dateFilterValue } =
  getPresetFiltersLocalStorage();

const initialState: {
  autocompletePlayers: Array<AutocompletePlayerModel>;

  playerToShowStats: {
    playername: string;
    pokersiteId: 1 | 2 | 3 | 4 | 5 | 6;
    pokersitePostfix: string;
    sameNickPlayers: Array<PlayerDto>;
  };

  limitStats: Array<{ limit: number } & Partial<ShortStatsDto>>;
  tableSizeStats: Array<{ tableSize: number } & ShortStatsDto>;
  monthStats: Array<{ month: string } & ShortStatsDto>;
  generalStats: Partial<FullStatsDto>;

  graphResults: PlayerGraphResultsDto;
  graphSpinner: boolean;

  tableSizeFilterValue?: number;
  limitFilterValue: number;
  dateFilterValue?: number;
  customDateFilterValue?: {
    fromDate: string;
    toDate: string;
  };

  nicknameToFind?: string;
  showMiningError: boolean;
  miningErrorPokersiteId: number;
} = {
  autocompletePlayers: [],
  playerToShowStats: undefined!,

  limitStats: [],
  tableSizeStats: [],
  monthStats: [],
  generalStats: undefined!,

  graphResults: undefined!,
  graphSpinner: false,

  tableSizeFilterValue,
  limitFilterValue,
  dateFilterValue,
  customDateFilterValue,
  showMiningError: false,
  miningErrorPokersiteId: undefined!,
};

export const statReducer = createSlice({
  name: 'stat',
  initialState,
  reducers: {
    showMiningError: (state, action: PayloadAction<number>) => {
      state.showMiningError = true;
      state.miningErrorPokersiteId = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(changePlayernameToFind, (state, { payload }) => {
      state.nicknameToFind = payload;
      if (payload === '') {
        state.autocompletePlayers = [];
      }
    });
    builder.addCase(changeLimitFilter, (state, { payload }) => {
      state.limitFilterValue = payload;
    });
    builder.addCase(changeTableSizeFilter, (state, { payload }) => {
      state.tableSizeFilterValue = payload;
    });
    builder.addCase(changeDateFilter, (state, { payload }) => {
      const { value, customValue } = payload;
      state.dateFilterValue = value;
      state.customDateFilterValue = customValue;
    });
    builder.addCase(setLoadedPlayers, (state, { payload }) => {
      state.autocompletePlayers = payload;
    });

    builder.addCase(setLoadedBaseStats, (state, { payload }) => {
      const {
        playername,
        pokersiteId,
        limitStats,
        tableSizeStats,
        generalStats,
        monthStats,
        sameNickPlayers = [],
      } = payload;

      state.nicknameToFind = '';

      document.title = playername;

      state.playerToShowStats = {
        playername,
        pokersiteId,
        pokersitePostfix: pokerSitePostfix[pokersiteId],
        sameNickPlayers,
      };
      state.tableSizeStats = tableSizeStats.sort((a, b) => b.tableSize - a.tableSize);
      state.limitStats = limitStats.sort((a, b) => a.limit - b.limit);
      state.monthStats = monthStats.sort((a, b) => +b.month - +a.month);
      state.generalStats = generalStats;
    });
    builder.addCase(setLoadedGraphStats, (state, { payload }) => {
      state.graphResults = payload;
      state.graphSpinner = false;
    });

    builder.addCase(showLoadingGraphSpinner, (state) => {
      state.graphSpinner = true;
    });
    builder.addCase(showGraphError, (state) => {
      state.graphSpinner = false;
    });
    builder.addCase(dropFilters, (state) => {
      state.limitFilterValue = 0;
      state.tableSizeFilterValue = undefined;
      state.dateFilterValue = undefined;
      state.customDateFilterValue = undefined;
    });
    builder.addCase(findStats, (state) => {
      state.showMiningError = false;
    });
    builder.addCase(dropFiltersToSettingsState, (state) => {
      const { tableSizeFilterValue, limitFilterValue, customDateFilterValue, dateFilterValue } =
        getPresetFiltersLocalStorage();

      state.limitFilterValue = limitFilterValue;
      state.tableSizeFilterValue = tableSizeFilterValue;
      state.dateFilterValue = dateFilterValue;
      state.customDateFilterValue = customDateFilterValue;
      state.autocompletePlayers = [];
    });
  },
});
